import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { getDashboardStatsApi } from "../../../services/DashboardService";

const MONTH_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const formatDate = (v) => {
  if (!v) return "-";
  return new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const Home = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDashboardStatsApi()
      .then((res) => setData(res?.data?.data || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const counts = data?.counts || {};
  const revenueChart = data?.revenueChart || { months: MONTH_ABBR.slice(-6), data: [0,0,0,0,0,0] };
  const usersChart = data?.usersChart || { customers: 0, consultants: 0 };
  const topConsultants = data?.topConsultants || [];
  const newCustomers = data?.newCustomers || [];

  const statCards = [
    { title: "All Customers", value: loading ? "..." : counts.totalCustomers ?? 0, icon: "la la-users", color: "primary", to: "all-customers" },
    { title: "All Consultants", value: loading ? "..." : counts.totalConsultants ?? 0, icon: "la la-user", color: "warning", to: "all-consultants" },
    { title: "All Sessions", value: loading ? "..." : counts.totalSessions ?? 0, icon: "la la-calendar", color: "info", to: "all-sessions" },
    { title: "Total Revenue", value: loading ? "..." : `₹${Number(counts.totalRevenue || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`, icon: "la la-inr", color: "success", to: "revenue-collection" },
  ];

  // Revenue bar chart options
  const revenueOptions = {
    chart: { type: "bar", height: 280, toolbar: { show: false } },
    plotOptions: { bar: { columnWidth: "40%", borderRadius: 4 } },
    colors: ["#6571ff"],
    dataLabels: { enabled: false },
    grid: { borderColor: "#f1f1f1", strokeDashArray: 4 },
    xaxis: {
      categories: revenueChart.months,
      labels: { style: { colors: "#888", fontSize: "12px" } },
      axisBorder: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: "#888", fontSize: "12px" },
        formatter: (v) => `₹${v >= 1000 ? (v / 1000).toFixed(1) + "k" : v}`,
      },
    },
    tooltip: { y: { formatter: (v) => `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` } },
  };
  const revenueSeries = [{ name: "Revenue", data: revenueChart.data }];

  // Users vs Consultants donut chart
  const donutOptions = {
    chart: { type: "donut", height: 280 },
    labels: ["Customers", "Consultants"],
    colors: ["#6571ff", "#ff7f50"],
    dataLabels: { enabled: true, style: { fontSize: "12px" } },
    legend: { position: "bottom" },
    plotOptions: { pie: { donut: { labels: { show: true, total: { show: true, label: "Total", formatter: (w) => w.globals.seriesTotals.reduce((a, b) => a + b, 0) } } } } },
    stroke: { width: 2 },
    responsive: [{ breakpoint: 480, options: { chart: { height: 220 } } }],
  };
  const donutSeries = [usersChart.customers, usersChart.consultants];

  return (
    <>
      {/* ── Stat Cards ── */}
      <div className="row mb-4">
        {statCards.map((c) => (
          <div className="col-xl-3 col-sm-6" key={c.title}>
            <Link to={c.to} style={{ textDecoration: "none" }}>
              <div className={`widget-stat card bg-${c.color}`}>
                <div className="card-body p-4">
                  <div className="media">
                    <span className="me-3">
                      <i className={`${c.icon} text-white`} style={{ fontSize: 32 }} />
                    </span>
                    <div className="media-body text-white">
                      <p className="mb-1" style={{ opacity: 0.85 }}>{c.title}</p>
                      <h3 className="text-white mb-0" style={{ fontSize: 22, fontWeight: 700 }}>{c.value}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>

      {/* ── Charts Row ── */}
      <div className="row mb-4">
        <div className="col-xl-7 col-lg-12">
          <div className="card h-100">
            <div className="card-header">
              <h4 className="card-title mb-0">Monthly Revenue (Last 6 Months)</h4>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="d-flex align-items-center justify-content-center" style={{ height: 280 }}>
                  <span className="spinner-border text-primary" />
                </div>
              ) : (
                <ReactApexChart options={revenueOptions} series={revenueSeries} type="bar" height={280} />
              )}
            </div>
          </div>
        </div>
        <div className="col-xl-5 col-lg-12">
          <div className="card h-100">
            <div className="card-header">
              <h4 className="card-title mb-0">Users vs Consultants</h4>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="d-flex align-items-center justify-content-center" style={{ height: 280 }}>
                  <span className="spinner-border text-primary" />
                </div>
              ) : (
                <ReactApexChart options={donutOptions} series={donutSeries} type="donut" height={280} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Top Consultants ── */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0">Top Consultants by Session Time</h4>
              <Link to="all-consultants" className="btn btn-xs btn-primary">View All</Link>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Consultant</th>
                      <th>Total Time</th>
                      <th>Sessions</th>
                      <th>Calls / Chats</th>
                      <th>Revenue Generated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={6} className="text-center py-4 text-muted">Loading...</td></tr>
                    )}
                    {!loading && topConsultants.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-4 text-muted">No session data yet.</td></tr>
                    )}
                    {!loading && topConsultants.map((c, i) => (
                      <tr key={c.consultantId || i}>
                        <td>
                          <span className={`badge badge-${["success","primary","warning","info","danger"][i] || "secondary"} light`}>
                            #{i + 1}
                          </span>
                        </td>
                        <td>
                          <span className="fw-semibold" style={{ textTransform: "capitalize" }}>{c.consultantName}</span>
                        </td>
                        <td>
                          {c.totalMinutes > 0 ? `${c.totalMinutes} min` : `${Math.floor((c.totalSeconds || 0) % 60)}s`}
                        </td>
                        <td>
                          <span className="badge badge-primary light">{c.totalSessions}</span>
                        </td>
                        <td>
                          <span className="badge badge-info dark me-1">
                            <i className="fa fa-phone me-1" />{c.callSessions}
                          </span>
                          <span className="badge badge-secondary light">
                            <i className="fa fa-comments me-1" />{c.chatSessions}
                          </span>
                        </td>
                        <td>
                          <span className="fw-bold text-success">
                            ₹{Number(c.totalRevenue).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── New Customers This Week ── */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div>
                <h4 className="card-title mb-0">New Customers This Week</h4>
                <div className="text-muted small">Signups in the last 7 days</div>
              </div>
              <Link to="all-customers" className="btn btn-xs btn-primary">View All</Link>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Wallet Balance</th>
                      <th>Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={6} className="text-center py-4 text-muted">Loading...</td></tr>
                    )}
                    {!loading && newCustomers.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-4 text-muted">No new signups this week.</td></tr>
                    )}
                    {!loading && newCustomers.map((u, i) => (
                      <tr key={u.id}>
                        <td className="text-muted small">{i + 1}</td>
                        <td>
                          <span className="fw-semibold" style={{ textTransform: "capitalize" }}>{u.name}</span>
                        </td>
                        <td className="text-muted small">{u.email}</td>
                        <td className="text-muted small">{u.phone}</td>
                        <td>
                          <span className="badge badge-success light">
                            ₹{Number(u.walletBalance).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </span>
                        </td>
                        <td className="text-muted small">{formatDate(u.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
